﻿/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import clsx from 'clsx'
import {useTranslation} from 'react-i18next'
import {CalendarDaysIcon} from '@heroicons/react/24/solid'
import {BuildingOffice2Icon, CubeTransparentIcon, DeviceTabletIcon, EnvelopeIcon, PhoneIcon, UserIcon, UsersIcon,} from '@heroicons/react/24/outline'
import {Reservation} from 'types/entities/Reservation'
import {ReservationOfferStatus} from 'types/enums/ReservationOfferStatus'
import {ReservationStatus} from 'types/enums/ReservationStatus'
import {getPackageCategoryName} from 'utils/packageUtils'
import {
    getReservationAccountName,
    getReservationContactEmail,
    getReservationContactName,
    getReservationContactPhone,
} from 'utils/reservations/reservationDisplayUtils'

type UiOfferReservationsForDateProps = {
    reservations: Reservation[]
    className?: string
}

const OPEN_OFFER_STATUSES: ReservationOfferStatus[] = [
    ReservationOfferStatus.Lead,
    ReservationOfferStatus.New,
    ReservationOfferStatus.Sent,
    ReservationOfferStatus.Reconfirm,
]

const BOOKED_RESERVATION_STATUSES: ReservationStatus[] = [ReservationStatus.Reserved, ReservationStatus.Confirmed]

const headerCellClass = 'px-2 py-1 text-left align-middle whitespace-nowrap'
const bodyCellClass = 'px-2 py-2.5 align-middle'

const UiOfferReservationsForDate: React.FC<UiOfferReservationsForDateProps> = ({ reservations, className }) => {
    const { t } = useTranslation()

    return (
        <div
            className={clsx(
                'UiOfferReservationsForDate bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 flex flex-col',
                'min-h-30 overflow-hidden grow',
                className
            )}
        >
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-100 dark:border-gray-800 shrink-0 bg-gray-50/50 dark:bg-gray-800/20">
                <CalendarDaysIcon className="size-3.5 text-blue-500 dark:text-blue-400" />
                <h3 className="text-[12px] font-black text-gray-900 dark:text-gray-100 uppercase tracking-tighter">
                    {t('Other reservations for this date')}
                </h3>
            </div>

            <div className="flex-1 min-h-0 overflow-auto w-full">
                <table className="w-full text-gray-900 dark:text-gray-100 table-fixed">
                    <thead className="sticky top-0 z-10 bg-gray-100 dark:bg-gray-900 text-[11px] font-medium uppercase tracking-wide text-gray-600 dark:text-gray-200">
                        <tr>
                            <th className={headerCellClass}>
                                <div className="flex items-center gap-1">
                                    <CalendarDaysIcon className="size-3 shrink-0" />
                                    <span>{t('Time')}</span>
                                </div>
                            </th>
                            <th className={clsx(headerCellClass, 'text-center')}>
                                <div className="flex items-center justify-center gap-1">
                                    <UsersIcon className="size-3 shrink-0" />
                                    <span>{t('Persons')}</span>
                                </div>
                            </th>
                            <th className={clsx(headerCellClass, 'text-center')}>
                                <span>{t('Offered (open)')}</span>
                            </th>
                            <th className={clsx(headerCellClass, 'text-center')}>
                                <span>{t('Booked')}</span>
                            </th>
                            <th className={headerCellClass}>
                                <div className="flex items-center gap-1">
                                    <BuildingOffice2Icon className="size-3 shrink-0" />
                                    <span>{t('Account')}</span>
                                </div>
                            </th>
                            <th className={headerCellClass}>
                                <div className="flex items-center gap-1">
                                    <UserIcon className="size-3 shrink-0" />
                                    <span>{t('Contact')}</span>
                                </div>
                            </th>
                            <th className={headerCellClass}>
                                <div className="flex items-center gap-1">
                                    <EnvelopeIcon className="size-3 shrink-0" />
                                    <span>{t('Contact Details')}</span>
                                </div>
                            </th>
                            <th className={headerCellClass}>
                                <div className="flex items-center gap-1">
                                    <CubeTransparentIcon className="size-3 shrink-0" />
                                    <span>{t('Ticket type')}</span>
                                </div>
                            </th>
                            <th className={headerCellClass}>
                                <div className="flex items-center gap-1">
                                    <DeviceTabletIcon className="size-3 shrink-0" />
                                    <span>{t('Src')}</span>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-950 text-[11px] zebra-rows">
                        {reservations.length === 0 ? (
                            <tr>
                                <td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                    {t('No records found.')}
                                </td>
                            </tr>
                        ) : (
                            reservations.map((reservation) => {
                                const isOpenOffer = OPEN_OFFER_STATUSES.includes(
                                    reservation.offer as ReservationOfferStatus
                                )
                                const isBooked = BOOKED_RESERVATION_STATUSES.includes(
                                    reservation.status as ReservationStatus
                                )
                                const accountName = getReservationAccountName(reservation)
                                const contactName = getReservationContactName(reservation)
                                const email = getReservationContactEmail(reservation)
                                const phone = getReservationContactPhone(reservation)
                                const categoryName = getPackageCategoryName(reservation.category)

                                return (
                                    <tr key={reservation.id}>
                                        <td className={bodyCellClass}>
                                            {String(reservation.hour).padStart(2, '0')}:
                                            {String(reservation.minute).padStart(2, '0')}
                                        </td>
                                        <td className={clsx(bodyCellClass, 'text-center font-extrabold text-blue-600 dark:text-blue-400')}>
                                            {reservation.persons}
                                        </td>
                                        <td className={clsx(bodyCellClass, 'text-center font-semibold text-amber-600 dark:text-amber-400')}>
                                            {isOpenOffer ? t('Yes') : '---'}
                                        </td>
                                        <td className={clsx(bodyCellClass, 'text-center font-semibold text-emerald-600 dark:text-emerald-400')}>
                                            {isBooked ? t('Reserved') : '---'}
                                        </td>
                                        <td className={bodyCellClass}>
                                            <div className="truncate font-medium" title={accountName}>
                                                {accountName}
                                            </div>
                                        </td>
                                        <td className={bodyCellClass}>
                                            <div className="truncate" title={contactName}>
                                                {contactName}
                                            </div>
                                        </td>
                                        <td className={bodyCellClass}>
                                            <div className="flex flex-col gap-0.5 min-w-0 text-gray-600 dark:text-gray-400">
                                                <div className="truncate" title={email}>
                                                    {email}
                                                </div>
                                                <div className="inline-flex items-center gap-1 truncate" title={phone}>
                                                    <PhoneIcon className="size-3 shrink-0" />
                                                    <span>{phone}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className={bodyCellClass}>
                                            <div className="truncate text-gray-600 dark:text-gray-400" title={categoryName ?? '---'}>
                                                {categoryName ?? '---'}
                                            </div>
                                        </td>
                                        <td className={bodyCellClass}>
                                            <div className="truncate text-gray-500 dark:text-gray-500">
                                                {reservation.origin}
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default UiOfferReservationsForDate
